import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Close, Storefront } from "@mui/icons-material";
import { initialCommerceForm, validateCommerceForm } from "../../model/commerceModel";
const labels = {
  tipocomercio: "Tipo de comercio",
  tipointegracion: "Tipo de integración",
  integracion: "Integración",
};
const actionStyle = {
  "&&": { background: "linear-gradient(110deg, #193d50 0%, #176b71 100%)", color: "#fff" },
  "&&.Mui-disabled": { bgcolor: "#e1e8ee", color: "#75889a" },
};
export default function CommerceDialog({
  open,
  commerce,
  attributes,
  availableComercios = [],
  loading = false,
  loadError,
  onRetry,
  onClose,
  onSave,
}) {
  const editing = !!commerce;
  const [form, setForm] = useState(() => initialCommerceForm(commerce));
  const [errors, setErrors] = useState({});
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const lock = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    if (open) {
      setForm(initialCommerceForm(commerce));
      setErrors({});
      setReview(false);
      setResult(null);
      setBusy(false);
      lock.current = false;
    }
  }, [open, commerce]);
  const change = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
    setResult(null);
  };
  const close = () => {
    if (!lock.current) onClose();
  };
  const prepare = (event) => {
    event.preventDefault();
    if (lock.current || loading || loadError || result?.success || result?.uncertain) return;
    const next = validateCommerceForm(form);
    setErrors(next);
    if (!Object.keys(next).length) setReview(true);
  };
  const save = async () => {
    if (lock.current || loading || result?.success || result?.uncertain) return;
    lock.current = true;
    setBusy(true);
    setResult(null);
    try {
      const response = await onSave(form);
      if (alive.current)
        setResult(
          response || {
            success: false,
            error: "No se recibió confirmación del guardado.",
            uncertain: true,
          }
        );
    } catch (err) {
      if (alive.current)
        setResult({ success: false, error: err.message || "No se pudo completar el guardado." });
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const options = {
    tipocomercio: attributes?.tipo_comercio || [],
    tipointegracion: attributes?.tipo_integración || [],
    integracion: attributes?.integracion || [],
  };
  const flag = (field, label, yes, no) => (
    <Box
      sx={{
        p: 2,
        border: `1px solid ${errors[field] ? "#c24452" : "#dce5ed"}`,
        borderRadius: 2,
        bgcolor: field === "activo" ? "#edf8f3" : "#f1effc",
      }}
    >
      <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 600, color: "#2e4d63" }}>
        {label}
      </Typography>
      <ToggleButtonGroup
        exclusive
        value={form[field]}
        onChange={(_, value) => {
          if (value !== null) change(field, value);
        }}
        disabled={busy || loading}
        fullWidth
        sx={{
          "& .MuiToggleButton-root": {
            minHeight: 42,
            fontSize: 12,
            color: "#476379",
            textTransform: "none",
          },
          "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover": {
            bgcolor:
              field === "activo"
                ? form[field]
                  ? "#207159"
                  : "#963b59"
                : form[field]
                ? "#51479c"
                : "#596d80",
            color: "#fff",
            fontWeight: 700,
            borderColor: "transparent",
          },
        }}
      >
        <ToggleButton value={true}>{yes}</ToggleButton>
        <ToggleButton value={false}>{no}</ToggleButton>
      </ToggleButtonGroup>
      {(errors[field] || form[field] === null) && (
        <Typography sx={{ fontSize: 12, color: "#b33d4c", mt: 1 }}>
          {errors[field] || "Sin información: selecciona una opción."}
        </Typography>
      )}
    </Box>
  );
  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "90dvh", bgcolor: "#f5f8fb" } }}
    >
      <DialogTitle
        sx={{
          p: 2.5,
          background: "linear-gradient(110deg, #193d50 0%, #176b71 100%)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Storefront />
        <Box sx={{ flex: 1 }}>
          <Typography component="h2" sx={{ fontSize: 19, fontWeight: 700, color: "#fff" }}>
            {review ? "Revisar configuración" : editing ? "Editar comercio" : "Agregar comercio"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#d6e5ee" }}>
            {editing
              ? `${commerce.nombre} · ID ${commerce.id}`
              : "Selecciona un comercio del catálogo y configura su integración"}
          </Typography>
        </Box>
        <IconButton
          aria-label="Cerrar formulario"
          disabled={busy}
          onClick={close}
          sx={{ color: "#fff" }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          p: { xs: 2, sm: 3 },
          "& .MuiOutlinedInput-root": { minHeight: 54, bgcolor: "#fff", fontSize: 14 },
          "& .MuiInputLabel-root": { color: "#506b80" },
          "& .MuiSelect-icon": { display: "block", color: "#59748a" },
        }}
      >
        {loading && (
          <Box sx={{ display: "flex", gap: 1.5, my: 2 }}>
            <CircularProgress size={22} />
            <Typography sx={{ fontSize: 14 }}>Cargando opciones…</Typography>
          </Box>
        )}
        {loadError && (
          <Alert
            severity="error"
            sx={{ my: 2 }}
            action={
              <Button disabled={loading || busy} color="inherit" onClick={onRetry}>
                Reintentar
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}
        {result && (
          <Alert
            sx={{ mt: 2, mb: 2 }}
            severity={
              result.success
                ? result.refreshWarning
                  ? "warning"
                  : "success"
                : result.uncertain
                ? "warning"
                : "error"
            }
          >
            {result.success
              ? result.refreshWarning || "Comercio guardado correctamente."
              : result.error}
            {result.uncertain
              ? " Actualiza el listado para comprobar el resultado antes de intentar guardarlo otra vez."
              : ""}
          </Alert>
        )}
        {review ? (
          <Box
            sx={{ mt: 2, p: 2.5, bgcolor: "#fff", border: "1px solid #dce5ed", borderRadius: 2 }}
          >
            <Typography sx={{ fontSize: 14, color: "#476379", mb: 2 }}>
              Verifica estos valores antes de {editing ? "actualizar" : "crear"} el comercio.
            </Typography>
            <Grid container spacing={2.5}>
              {[
                ["Comercio", `${form.namecomercio} · ID ${form.idcommerce}`],
                ["Tipo de comercio", form.tipocomercio],
                ["Tipo de integración", form.tipointegracion],
                ["Integración", form.integracion],
                ["Estado", form.activo ? "Activo" : "Inactivo"],
                ["Apertura CB", form.aperturacb ? "Habilitada" : "Deshabilitada"],
              ].map(([label, value]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Typography sx={{ fontSize: 12, color: "#657d91", mb: 0.5 }}>{label}</Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#254459",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {value}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <Box component="form" id="commerce-form" onSubmit={prepare} sx={{ mt: 3 }}>
            {!editing && (
              <Autocomplete
                options={availableComercios}
                value={availableComercios.find((row) => String(row.id) === form.idcommerce) || null}
                getOptionLabel={(row) => `${row.nombre} · ID ${row.id}`}
                isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                disabled={loading || busy || !!loadError}
                onChange={(_, value) => {
                  setForm((prev) => ({
                    ...prev,
                    idcommerce: value ? String(value.id) : "",
                    namecomercio: value?.nombre || "",
                  }));
                  setErrors((prev) => ({ ...prev, idcommerce: null }));
                }}
                noOptionsText="No hay comercios disponibles"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Comercio del catálogo"
                    required
                    error={!!errors.idcommerce}
                    helperText={
                      errors.idcommerce ||
                      "Busca por nombre o ID. El nombre y el ID se toman del catálogo."
                    }
                  />
                )}
              />
            )}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="ID del comercio"
                  value={form.idcommerce}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={9}>
                <TextField
                  fullWidth
                  label="Nombre del comercio"
                  value={form.namecomercio}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {Object.entries(labels).map(([field, label]) => (
                <Grid item xs={12} sm={field === "integracion" ? 12 : 6} key={field}>
                  <TextField
                    select
                    required
                    fullWidth
                    disabled={loading || busy || !!loadError}
                    label={label}
                    name={field}
                    value={form[field]}
                    error={!!errors[field]}
                    helperText={errors[field]}
                    onChange={(event) => change(field, event.target.value)}
                  >
                    {[...new Set([...options[field], ...(form[field] ? [form[field]] : [])])].map(
                      (value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                </Grid>
              ))}
              <Grid item xs={12} sm={6}>
                {flag("activo", "Estado del comercio", "Activo", "Inactivo")}
              </Grid>
              <Grid item xs={12} sm={6}>
                {flag("aperturacb", "Apertura CB", "Habilitada", "Deshabilitada")}
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, bgcolor: "#fff", borderTop: "1px solid #dce5ed" }}>
        <Button onClick={close} disabled={busy} sx={{ "&&": { color: "#365b76" } }}>
          {result?.success || result?.uncertain ? "Cerrar" : "Cancelar"}
        </Button>
        {review && !result?.success && !result?.uncertain && (
          <Button
            disabled={busy}
            onClick={() => {
              setReview(false);
              setResult(null);
            }}
            sx={{ "&&": { color: "#365b76" } }}
          >
            Volver a editar
          </Button>
        )}
        {!result?.success && !result?.uncertain && (
          <Button
            variant="contained"
            type={review ? "button" : "submit"}
            form={review ? undefined : "commerce-form"}
            onClick={review ? save : undefined}
            disabled={busy || loading || !!loadError}
            sx={actionStyle}
          >
            {busy ? <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} /> : null}
            {review ? (editing ? "Confirmar cambios" : "Confirmar creación") : "Revisar cambios"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
CommerceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  commerce: PropTypes.object,
  attributes: PropTypes.object,
  availableComercios: PropTypes.array,
  loading: PropTypes.bool,
  loadError: PropTypes.string,
  onRetry: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
